import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card } from 'react-bootstrap';
import Loading from '../components/loading';
import SuccessMessage from '../components/successMessage';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { useNavigate } from 'react-router-dom';
import {
  FormControl,
  Autocomplete,
  TextField,
  Paper,
  Box,
  Button,
  ButtonGroup,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventNoteIcon from '@mui/icons-material/EventNote';
import Alert from '@mui/material/Alert';
import dayjs from 'dayjs';
import BusinessHoursLegend from './businessHoursLegend';
import ProfessionalModal from '../register/professional/professionalModal';
import AgendaBlockModal from './agendaBlock/agendaBlockModal';
import PersonalEventModal from './personalEvent/personalEventModal';
import { useProfessionals } from '../hooks/useProfessionals';
import { useProfessionalsList } from '../register/professional/hooks/useProfessionalsList';
import { useRoomFilter } from './hooks/useRoomFilter';
import { useHideInactiveAppointments } from './hooks/useHideInactiveAppointments';
import { useCalendarAppointments } from './hooks/useCalendarAppointments';
import { useAgendaBlocks } from './hooks/useAgendaBlocks';
import { useAgendaBlock } from './hooks/useAgendaBlock';
import { usePersonalEvents } from './hooks/usePersonalEvents';
import { usePersonalEvent } from './hooks/usePersonalEvent';
import { useBusinessHours } from './hooks/useBusinessHours';
import { toBlockEvent } from './agendaBlock/toBlockEvent';
import { toPersonalEvent } from './personalEvent/toPersonalEvent';
import { toAppointmentEvent } from './toAppointmentEvent';
import AppointmentStatusLegend from './appointmentStatusLegend';
import EventTooltip, { getStatusText } from './eventTooltip';
import RoomsDayView from './roomsDay/roomsDayView';
import '../css/fullcalendar-dark.css';
import '../css/fullcalendar-mui.css';

const ALL_DENTISTS_OPTION = { id: '', nome: 'Todos os profissionais' };

function Agenda() {
  const navigate = useNavigate();
  const calendarRef = useRef(null);

  const [selectedDentistId, setSelectedDentistId] = useState('');
  const [isDentistModalOpen, setDentistModalOpen] = useState(false);
  const [isBlockModalOpen, setBlockModalOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  const [isPersonalEventModalOpen, setPersonalEventModalOpen] = useState(false);
  const [selectedPersonalEvent, setSelectedPersonalEvent] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [currentView, setCurrentView] = useState(
    () => sessionStorage.getItem('agendaView') || 'timeGridWeek',
  );
  const [calendarTitle, setCalendarTitle] = useState('');
  const [viewDate, setViewDate] = useState(() => {
    const savedDate = sessionStorage.getItem('agendaDate');
    return savedDate ? dayjs(savedDate) : dayjs();
  });

  const isRoomsView = currentView === 'timeGridDay';

  // Preserva a visão e a data ao navegar para /appointment e voltar.
  useEffect(() => {
    sessionStorage.setItem('agendaView', currentView);
  }, [currentView]);

  useEffect(() => {
    sessionStorage.setItem('agendaDate', dayjs(viewDate).toISOString());
  }, [viewDate]);

  const { professionals: dentists, refetch: refetchDentists } = useProfessionals();
  // /dentists/combobox não traz workingHours; a lista completa alimenta o destaque
  // de expediente do profissional selecionado no calendário.
  const { professionals: dentistsWithWorkingHours, refetch: refetchDentistsWithWorkingHours } =
    useProfessionalsList();
  const {
    rooms,
    roomLabel,
    selectedRoomId,
    setSelectedRoomId,
    selectedRoom,
    isRoomFilterVisible,
    clearRoomFilter,
    filterAppointmentsByRoom,
    filterBlocksByRoom,
  } = useRoomFilter();
  const {
    appointments: rawAppointments,
    isLoading,
    isStale,
    updatedAt,
  } = useCalendarAppointments(selectedDentistId);
  const { agendaBlocks: rawBlocks, refetch: refetchBlocks } = useAgendaBlocks(selectedDentistId);
  const { fetchAgendaBlock } = useAgendaBlock();
  const { personalEvents: rawPersonalEvents, refetch: refetchPersonalEvents } =
    usePersonalEvents(selectedDentistId);
  const { fetchPersonalEvent } = usePersonalEvent();
  const {
    isHidingInactiveAppointments,
    setHidingInactiveAppointments,
    filterInactiveAppointments,
  } = useHideInactiveAppointments();
  const businessHours = useBusinessHours(dentistsWithWorkingHours, selectedDentistId);

  const calendarEvents = useMemo(
    () => [
      ...filterInactiveAppointments(filterAppointmentsByRoom(rawAppointments)).map(
        toAppointmentEvent,
      ),
      ...filterBlocksByRoom(rawBlocks).map(toBlockEvent),
      // O recorte por profissional já vem do hook; o filtro de sala não se aplica.
      ...rawPersonalEvents.map(toPersonalEvent),
    ],
    [
      rawAppointments,
      rawBlocks,
      rawPersonalEvents,
      filterAppointmentsByRoom,
      filterBlocksByRoom,
      filterInactiveAppointments,
    ],
  );

  // Na visão por sala o filtro reduz as colunas exibidas, não os eventos.
  const visibleRooms = useMemo(
    () => (selectedRoom ? [selectedRoom] : rooms),
    [rooms, selectedRoom],
  );

  const allRoomsOption = useMemo(() => ({ id: '', name: roomLabel.allOption }), [roomLabel]);

  // Com um único profissional cadastrado o filtro não tem função, mesma regra do
  // filtro de sala. Sem nenhum profissional ele continua visível: o combo carrega
  // o atalho "+ Adicionar profissional".
  const isDentistFilterVisible = dentists.length !== 1;

  // O aviso é sobre o filtro de sala, então olha só o recorte por sala: esconder
  // canceladas e faltas não deve sugerir que a sala está vazia.
  const hasFilteredEverythingOut =
    selectedRoomId !== '' &&
    rawAppointments.length > 0 &&
    filterAppointmentsByRoom(rawAppointments).length === 0;

  function handleNewDentistSaved() {
    refetchDentists();
    refetchDentistsWithWorkingHours();
  }

  function handleBlockSaved(returnFromApi) {
    setSuccessMessage(String(returnFromApi?.data ?? 'Bloqueio salvo com sucesso.'));
    setShowSuccessMessage(true);
    refetchBlocks();
  }

  function handleBlockDeleted(returnFromApi) {
    setSuccessMessage(String(returnFromApi?.data ?? 'Bloqueio excluído com sucesso.'));
    setShowSuccessMessage(true);
    refetchBlocks();
  }

  function handlePersonalEventSaved(returnFromApi) {
    setSuccessMessage(String(returnFromApi?.data ?? 'Compromisso salvo com sucesso.'));
    setShowSuccessMessage(true);
    refetchPersonalEvents();
  }

  function handlePersonalEventDeleted(returnFromApi) {
    setSuccessMessage(String(returnFromApi?.data ?? 'Compromisso excluído com sucesso.'));
    setShowSuccessMessage(true);
    refetchPersonalEvents();
  }

  const goToEvent = (event) => {
    const { blockId, consultaId, personalEventId } = event?.event?.extendedProps ?? {};

    if (personalEventId) {
      fetchPersonalEvent(personalEventId)
        .then((returnFromApi) => {
          setSelectedPersonalEvent(returnFromApi.data);
          setPersonalEventModalOpen(true);
        })
        .catch((returnFromApi) => {
          console.error('ops! ocorreu um erro' + returnFromApi);
        });
      return;
    }

    if (blockId) {
      fetchAgendaBlock(blockId)
        .then((returnFromApi) => {
          setSelectedBlock(returnFromApi.data);
          setBlockModalOpen(true);
        })
        .catch((returnFromApi) => {
          console.error('ops! ocorreu um erro' + returnFromApi);
        });
      return;
    }

    if (consultaId) {
      navigate('/appointment/'.concat(consultaId));
    }
  };

  const handleDateClick = (event) => {
    // Clique num horário vago inicia uma nova consulta.
    const hour = String(event.date.getHours()).padStart(2, '0');
    const minute = String(event.date.getMinutes()).padStart(2, '0');
    navigate('/appointment', {
      state: { appointmentData: { hour: `${hour}:${minute}`, date: event.date } },
    });
  };

  // Clique num horário vago na visão por sala: já leva a sala pré-selecionada.
  const handleRoomSlotClick = (event, roomId) => {
    const hour = String(event.date.getHours()).padStart(2, '0');
    const minute = String(event.date.getMinutes()).padStart(2, '0');
    navigate('/appointment', {
      state: { appointmentData: { hour: `${hour}:${minute}`, date: event.date, roomId } },
    });
  };

  const handleDatesSet = (dateInfo) => {
    setCalendarTitle(dateInfo.view.title);
    setViewDate(dayjs(dateInfo.view.calendar.getDate()));
  };

  const handlePrev = () => {
    if (isRoomsView) {
      setViewDate((date) => date.subtract(1, 'day'));
      return;
    }
    calendarRef.current?.getApi().prev();
  };

  const handleNext = () => {
    if (isRoomsView) {
      setViewDate((date) => date.add(1, 'day'));
      return;
    }
    calendarRef.current?.getApi().next();
  };

  const handleToday = () => {
    if (isRoomsView) {
      setViewDate(dayjs());
      return;
    }
    calendarRef.current?.getApi().today();
  };

  const handleViewChange = (_, newView) => {
    if (!newView) return;
    // Ao entrar na visão "Dia" (por sala), fixa a data atual do calendário.
    if (newView === 'timeGridDay') {
      const apiDate = calendarRef.current?.getApi().getDate();
      setViewDate(dayjs(apiDate ?? new Date()));
      setCurrentView(newView);
      return;
    }
    setCurrentView(newView);
    calendarRef.current?.getApi().changeView(newView);
  };

  const renderEventContent = (eventInfo) => {
    const { status, statusDescription } = eventInfo.event.extendedProps;
    const isMonthView = eventInfo.view.type === 'dayGridMonth';
    const eventColor = eventInfo.event.backgroundColor;

    return (
      <EventTooltip event={eventInfo.event}>
        <Box sx={{ px: 0.5, overflow: 'hidden', lineHeight: 1.3 }}>
          <Box
            sx={{
              fontWeight: 600,
              fontSize: '0.8rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: isMonthView ? eventColor : 'inherit',
            }}
          >
            {eventInfo.event.title}
          </Box>
          {status && !isMonthView && (
            <Box sx={{ fontSize: '0.7rem', opacity: 0.9, fontStyle: 'italic' }}>
              {getStatusText({ status, statusDescription })}
            </Box>
          )}
        </Box>
      </EventTooltip>
    );
  };

  return (
    <Card className="rounded-4 shadow-lg border-0">
      <Loading isToHidden={isLoading === false} />
      {showSuccessMessage && (
        <SuccessMessage
          message={successMessage}
          show={showSuccessMessage}
          setShow={setShowSuccessMessage}
        />
      )}

      <Card.Body>
        {isStale && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Sem conexão com o servidor. Exibindo os últimos dados salvos
            {updatedAt ? ` (${dayjs(updatedAt).format('DD/MM/YYYY HH:mm')})` : ''}. Pode haver
            agendamentos mais recentes não exibidos.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          {isDentistFilterVisible && (
            <FormControl size="small" sx={{ flex: 1, minWidth: 250 }}>
              <Autocomplete
                disableClearable
                size="small"
                options={[ALL_DENTISTS_OPTION, ...dentists]}
                getOptionLabel={(option) => option.nome || option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={dentists.find((d) => d.id === selectedDentistId) ?? ALL_DENTISTS_OPTION}
                onChange={(_, newValue) => setSelectedDentistId(newValue?.id ?? '')}
                noOptionsText="Nenhum profissional encontrado."
                PaperComponent={({ children, ...other }) => (
                  <Paper {...other}>
                    {children}
                    <Box
                      sx={{ borderTop: '1px solid', borderColor: 'divider' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <Button fullWidth size="small" onClick={() => setDentistModalOpen(true)}>
                        + Adicionar profissional
                      </Button>
                    </Box>
                  </Paper>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="dentist-select"
                    label="Selecione o Profissional"
                    inputProps={{ ...params.inputProps, 'data-cy': 'dentist-filter' }}
                  />
                )}
              />
            </FormControl>
          )}

          {isRoomFilterVisible && (
            <FormControl size="small" sx={{ flex: 1, minWidth: 250 }}>
              <Autocomplete
                disableClearable
                size="small"
                options={[allRoomsOption, ...rooms]}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={selectedRoom ?? allRoomsOption}
                onChange={(_, newValue) => setSelectedRoomId(newValue?.id ?? '')}
                noOptionsText={roomLabel.emptyMessage}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id="room-select"
                    label={`Selecione o ${roomLabel.singular}`}
                    inputProps={{ ...params.inputProps, 'data-cy': 'room-filter' }}
                  />
                )}
              />
            </FormControl>
          )}
        </Box>

        {/* Toolbar MUI customizada */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <ButtonGroup size="small" variant="outlined">
              <Button
                data-testid="calendar-prev"
                onClick={handlePrev}
                sx={{ px: 0.5, minWidth: 36 }}
              >
                <ChevronLeftIcon fontSize="small" />
              </Button>
              <Button
                data-testid="calendar-next"
                onClick={handleNext}
                sx={{ px: 0.5, minWidth: 36 }}
              >
                <ChevronRightIcon fontSize="small" />
              </Button>
            </ButtonGroup>
            <Button size="small" variant="outlined" onClick={handleToday} sx={{ ml: 1 }}>
              Hoje
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<EventBusyIcon fontSize="small" />}
              onClick={() => {
                setSelectedBlock(null);
                setBlockModalOpen(true);
              }}
              sx={{ ml: 1 }}
            >
              Bloquear Agenda
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<EventNoteIcon fontSize="small" />}
              onClick={() => {
                setSelectedPersonalEvent(null);
                setPersonalEventModalOpen(true);
              }}
              sx={{ ml: 1 }}
            >
              Compromisso Pessoal
            </Button>
          </Box>

          <Typography
            data-testid="calendar-title"
            variant="subtitle1"
            fontWeight={600}
            sx={{ flexGrow: 1, textAlign: 'center' }}
          >
            {isRoomsView ? dayjs(viewDate).format('dddd, DD/MM/YYYY') : calendarTitle}
          </Typography>

          <FormControlLabel
            sx={{ mr: 1 }}
            control={
              <Checkbox
                size="small"
                data-cy="hide-inactive-appointments"
                checked={isHidingInactiveAppointments}
                onChange={(event) => setHidingInactiveAppointments(event.target.checked)}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                Esconder canceladas e faltas
              </Typography>
            }
          />

          <ToggleButtonGroup
            size="small"
            value={currentView}
            exclusive
            onChange={handleViewChange}
            sx={{ '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5 } }}
          >
            <ToggleButton value="dayGridMonth">Mês</ToggleButton>
            <ToggleButton value="timeGridWeek">Semana</ToggleButton>
            <ToggleButton value="timeGridDay">Dia</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {hasFilteredEverythingOut && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={clearRoomFilter}>
                {roomLabel.allOption}
              </Button>
            }
          >
            Nenhuma consulta neste período para {selectedRoom?.name}.
          </Alert>
        )}

        {isRoomsView ? (
          <RoomsDayView
            filterAppointments={filterInactiveAppointments}
            date={viewDate}
            rooms={visibleRooms}
            roomLabel={roomLabel}
            onEventClick={goToEvent}
            onDateClick={handleRoomSlotClick}
          />
        ) : (
          <>
            <AppointmentStatusLegend />

            {businessHours && <BusinessHoursLegend />}

            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
              initialView={currentView}
              initialDate={dayjs(viewDate).toDate()}
              locale="pt-br"
              nowIndicator={true}
              editable={false}
              headerToolbar={false}
              businessHours={businessHours}
              slotMinTime="06:00:00"
              slotMaxTime="21:00:00"
              contentHeight="auto"
              allDayText="Dia Inteiro"
              events={calendarEvents}
              datesSet={handleDatesSet}
              eventContent={renderEventContent}
              dateClick={handleDateClick}
              eventClick={goToEvent}
            />
          </>
        )}
      </Card.Body>
      <ProfessionalModal
        open={isDentistModalOpen}
        onClose={() => setDentistModalOpen(false)}
        onSave={handleNewDentistSaved}
      />
      <AgendaBlockModal
        open={isBlockModalOpen}
        onClose={() => {
          setBlockModalOpen(false);
          setSelectedBlock(null);
        }}
        onSave={handleBlockSaved}
        onDelete={handleBlockDeleted}
        agendaBlock={selectedBlock}
      />
      <PersonalEventModal
        open={isPersonalEventModalOpen}
        onClose={() => {
          setPersonalEventModalOpen(false);
          setSelectedPersonalEvent(null);
        }}
        onSave={handlePersonalEventSaved}
        onDelete={handlePersonalEventDeleted}
        personalEvent={selectedPersonalEvent}
        defaultDate={viewDate}
        defaultProfessionalId={selectedDentistId}
      />
    </Card>
  );
}

export default Agenda;
